import {useMutation} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {Error, Success} from 'src/feature/bite/helpers/notification'
import {useAuthUser} from 'react-auth-kit'
import {useVIP} from '../hook'

import useRequestParams from 'src/feature/bite/hook/request'

import {
  createCampaignFile,
  createCampaignPoint,
  createCampaignPriceCluster,
  createCampaignProduct,
  deleteCampaignFile,
  deleteCampaignProduct,
  deleteExcludeCampaignProduct,
} from 'src/feature/bite/context/useMutation'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import {CAMPAIGN_API} from 'src/feature/bite/domain'

const api = `${CAMPAIGN_API}/VIPTier`
const apiProgram = `${CAMPAIGN_API}/VIPProgram`
const apiCampaign = `${CAMPAIGN_API}/Campaign`
const apiCampaignProduct = `${CAMPAIGN_API}/CampaignProduct`
const apiCampaignVIPTier = `${CAMPAIGN_API}/CampaignVIPTier`
const apiFile = `${CAMPAIGN_API}/File`

export function useCreateVIPTier() {
  const auth = useAuthUser()
  const router = useNavigate()
  const {headers, errors} = useRequestParams()

  const userID = auth()?.id

  return useMutation(
    ['CreateVIPTier'],
    async ({values}: {values: any}) => {
      const reqBody = {
        language: headers.Language,
        userID,
        vipTier: values,
        // storeID,
      }

      const Response = await fetch(`${api}/CreateVIPTier`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const Data = await Response.json()

      if (!Data?.result?.isSuccess) {
        Error(Data?.result?.errorMessage || errors.vipTier.create_error)
        return false
      }

      Success(errors.vipTier.create_success)

      setTimeout(() => {
        router(`/vip/edit/${Data?.id}`)
      }, 1000)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.vipTier.create_error)
      },
    }
  )
}

export function useUpdateVIPTier() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  const userID = auth()?.id

  return useMutation(
    ['VIPTierUpdate'],
    async ({values}: {values: any}) => {
      const Response = await fetch(`${api}/UpdateVIPTier`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: headers.Language,
          vipTier: values,
          userID,
          // storeID: '996225251718',
        }),
      })

      const Data = await Response.json()

      if (!Data.result.isSuccess) {
        Error(Data?.result?.errorMessage || errors.vipTier.update_error)
        return false
      }

      Success(errors.vipTier.update_success)

      /*    setTimeout(() => {
        router(`/vip/edit/${values.id}`)
      }, 1000) */

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.vipTier.update_error)
      },
    }
  )
}

export function useDeleteVIPTier() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  const userID = auth()?.id

  return useMutation(
    ['VIPTierDelete'],
    async ({id}: {id: string}) => {
      const Response = await fetch(`${api}/DeleteVIPTier`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: headers.Language,
          vipTierID: id,
          userID,
        }),
      })

      const Data = await Response.json()

      if (!Data.result.isSuccess) {
        Error(Data?.result?.errorMessage || errors.vipTier.delete_error)
        return false
      }

      Success(errors.vipTier.delete_success)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.vipTier.delete_error)
      },
    }
  )
}

export function useUpdateVIPProgram() {
  const auth = useAuthUser()
  const router = useNavigate()
  const {headers, errors} = useRequestParams()

  const userID = auth()?.id

  return useMutation(
    ['VIPProgramUpdate'],
    async ({values}: {values: any}) => {
      const Response = await fetch(`${apiProgram}/UpdateVIPProgram`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          vipProgram: values,
          // storeID: '996225251718',
        }),
      })

      const Data = await Response.json()

      if (!Data.result.isSuccess) {
        Error(Data?.result?.errorMessage || errors.vipProgram.update_error)
        return false
      }

      Success(errors.vipProgram.update_success)

      setTimeout(() => {
        router(`/vip/`)
      }, 1000)

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error(errors.vipProgram.update_error)
      },
    }
  )
}

export function useCreateVIPCampaign() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  const timeZoneOffSet = getTimeZone()

  return useMutation(
    ['CreateReferralCampaign'],
    async ({
      values,
      tierID,
      products,
      uploadFileIdsState,
    }: {
      values: any
      tierID: string
      products: string[]
      uploadFileIdsState: any
    }) => {
      const reqBody = {
        userID: auth().id,
        language: headers.Language,
        campaign: {
          name: values.name,
          description: values.description,
          status: 1,
          timeZoneOffSet,
          startDate: '2024-01-01T16:40:39.265Z',
          endDate: '2100-01-01T13:54:48.978Z',
          workingDays: [0, 1, 2, 3, 4, 5, 6, 7],
          workingStartTime: null,
          workingEndTime: null,
          promoCluster: values.promoCluster,
          promoType: values.promoType,
          promoGroup: values.promoGroup,
          priceStrategy: 0,
          isRepeatAction: true,
          minAmountEqualValue: 0,
          discountGroup: 0,
          maxBenefitAmount: 0,
          amountLimit: 0,
          countLimit: 0,
          order: 10,
          sub: 10,
          super: 10,
          maxOfBenefit: 0,
          periodType: 0,
          workerPeriodType: 0,
          periodValue: 0,
          workerPeriodValue: 0,
          minOfDailyTX: 0,
          maxOfDailyTX: 0,
          maxOfBenefitAllowedPerDay: 0,
          maxOfBenefitAllowedPerTX: 0,
          maxOfTrxAllowed: 0,
          amount: 0,
          amountCalculateType: 0,
          periodBenefit: 0,
          basketCount: 0,
          isDifferentDays: true,
          totalBenefitAmount: 0,
          totalRemainingBenefitAmount: 0,
          vipTierCampaign: true,
        },
      }

      const Response = await fetch(`${apiCampaign}/CreateCampaign`, {
        method: 'POST',
        headers,
        body: JSON.stringify(reqBody),
      })

      const Data = await Response.json()

      const campaignID = Data?.campaignID

      await Promise.all([
        await createCampaignPoint(
          auth().id,
          headers,
          campaignID,
          Number(values.fixedValue),
          Number(values.pointsToBeEarned),
          Number(values.amountToBeSpent)
        ),
        await createCampaignPriceCluster(auth().id, headers, campaignID, values.priceCluster),
        await createCampaignVIPTier(auth().id, headers, campaignID, tierID),
      ])

      if (values.productInSelectionType === '1') {
        await createCampaignProduct(auth().id, headers, campaignID, ['All'])
      }

      if (values.productInSelectionType === '2') {
        await createCampaignProduct(auth().id, headers, campaignID, products)
      }

      if (values.productInSelectionType === '3') {
        await Promise.all([
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      if (values.productOutSelectionType === '3') {
        await Promise.all([
          await createCampaignProduct(auth().id, headers, campaignID, ['All']),
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      return true
    },
    {
      onSuccess: () => {
        Success(errors.campaign.create_success)
      },
      onError: (error) => {
        Error(errors.campaign.create_error)
      },
    }
  )
}

export function useChangeStatusCampaign() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  return useMutation(
    ['ChangeStatusCampaign'],
    async ({campaignID, status}: {campaignID: string; status: number}) => {
      const Response = await fetch(`${apiCampaign}/UpdateCampaignStatus`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth().id,
          language: headers.Language,
          campaignID,
          status,
        }),
      })

      const Data = await Response.json()

      if (!Data.result.isSuccess) {
        Error(Data?.result?.errorMessage || errors.campaign.status_error)
        return false
      }

      return true
    },
    {
      onSuccess: () => {
        Success(errors.campaign.status_success)
      },
      onError: (error) => {
        Error(errors.campaign.status_error)
      },
    }
  )
}

export function useDeleteVIPCampaign() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  const userID = auth()?.id

  return useMutation(
    ['DeleteReferralCampaign'],
    async ({campaignID, tierID}: {campaignID: string; tierID: string}) => {
      const Response = await fetch(`${apiCampaignVIPTier}/DeleteCampaignVIPTier`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
          vipTierIDList: [tierID],
          campaignID,
        }),
      })

      const Data = await Response.json()

      if (!Data.result.isSuccess) {
        Error(Data?.result?.errorMessage || errors.campaign.delete_error)
        return false
      }

      return true
    },
    {
      onSuccess: () => {
        Success(errors.campaign.delete_success)
      },
      onError: (error) => {
        Error(errors.campaign.delete_error)
      },
    }
  )
}

export function useUpdateVIPCampaign() {
  const auth = useAuthUser()
  const {headers, errors} = useRequestParams()

  return useMutation(
    ['UpdateReferralCampaign'],
    async ({
      values,
      campaignID,
      product,
      uploadFileIdsState,
      deleteFileIdsState,
    }: {
      values: any
      campaignID: string
      product: {
        removedProductsId: string[]
        addedProductsId: string[]
      }
      uploadFileIdsState: {name: string; id: string}[]
      deleteFileIdsState: string[]
    }) => {
      if (values.productInSelectionType === '1') {
        await Promise.all([
          await createCampaignProduct(auth().id, headers, campaignID, ['All']),
          await deleteExcludeCampaignProduct(auth().id, headers, campaignID, ['All']),
        ])
      }

      if (values.productInSelectionType === '2') {
        await Promise.all([
          await deleteCampaignProduct(auth().id, headers, campaignID, [
            // 'All',
            ...product.removedProductsId,
          ]),
          await createCampaignProduct(auth().id, headers, campaignID, product.addedProductsId),
          await deleteExcludeCampaignProduct(auth().id, headers, campaignID, ['All']),
        ])
      }

      if (values.productInSelectionType === '3') {
        await Promise.all([
          await deleteCampaignProduct(auth().id, headers, campaignID, ['All']),
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      if (values.productOutSelectionType === '3') {
        await Promise.all([
          await createCampaignProduct(auth().id, headers, campaignID, ['All']),
          await createCampaignFile(auth().id, headers, campaignID, uploadFileIdsState),
        ])
      }

      if (deleteFileIdsState.length > 0) {
        await deleteCampaignFile(auth().id, headers, campaignID, deleteFileIdsState)
      }
    },
    {
      onSuccess: () => {
        Success(errors.campaign.update_success)
      },
      onError: (error) => {
        Error(errors.campaign.update_error)
      },
    }
  )
}

export function useUploadFileByType() {
  const auth = useAuthUser()
  const {headers} = useRequestParams()
  const {uploadFileIdsState, uploadFileIdsSet, uploadProductsSet} = useVIP()

  return useMutation(
    ['UploadFile'],
    async ({file, type}: {file: any; type: number}) => {
      const formData = new FormData()
      formData.append('language', headers.Language)
      formData.append('userID', auth().id)
      formData.append('file', file)
      formData.append('type', type.toString())

      const Response = await fetch(`${apiFile}/UploadFile`, {
        method: 'POST',
        headers: {Authorization: headers.Authorization},
        body: formData,
      })

      const Data = await Response.json()

      const ResponseProduct = await fetch(`${apiCampaignProduct}/GetAllCampaignProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: headers.Language,
          userID: auth().id,
          campaignID: '',
          fileIDList: [Data?.fileID],
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      const DataProduct = await ResponseProduct.json()

      uploadFileIdsSet([
        ...uploadFileIdsState,
        {
          name: file.name,
          id: Data?.fileID,
          type,
        },
      ])

      const productsAddType = DataProduct.data.map((product) => {
        return {
          ...product,
          type,
        }
      })

      uploadProductsSet(productsAddType)

      return Data
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        // Error(errors.referralProgram.upload_error)
      },
    }
  )
}

export function useStartManuelProcess() {
  const auth = useAuthUser()
  const {headers} = useRequestParams()

  return useMutation(
    ['StartManuelProcess'],
    async () => {
      const Response = await fetch(`${apiProgram}/StartManuelProcess`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          language: headers.Language,
          userID: auth().id,
          // storeID: '996225251718',
        }),
      })

      const Data = await Response.json()

      if (!Data.result.isSuccess) {
        Error(Data?.result?.errorMessage)
        return false
      }

      Success('Process started successfully')

      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        Error('Process start failed')
      },
    }
  )
}

export const createCampaignVIPTier = async (
  userID: string,
  headers: any,
  campaignID: string,
  tierID: string
) => {
  const Response = await fetch(`${apiCampaignVIPTier}/CreateCampaignVIPTier`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      language: headers.Language,
      userID,
      campaignID,
      vipTierIDList: [tierID],
    }),
  })

  const Data = await Response.json()

  return Data
}
